import React from 'react';
import { Chip } from '@mui/material';
import { Text, Theme } from '@simplybenefits/sb-ui-library';
import { helpers } from "@simplybenefits/sb-utils";
import UsageBlock from './UsageBlock.js';
import Tooltip from '@mui/material/Tooltip';
import { Info as InfoIcon } from '@mui/icons-material';

/**
 * @param usage
 * @param coverage
 * @param fillColor
 * @param waivedEHC
 * @returns {JSX.Element}
 * @constructor
 */
const HealthUsageBlock = ({ usage, coverage, fillColor, waivedEHC }) => {
    const { isCombinedMaximum } = coverage;

    return (
        <React.Fragment>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <div>
                    <Text
                        medium
                        bold
                        customStyle={{ 
                            flex: 1,
                            display: 'inline',
                            margin: '0 0 10px 0'
                        }}
                    >
                        Major Medical
                    </Text>
                    {
                        waivedEHC &&
                            <Chip
                                size="small"
                                label="Waived"
                                color="primary"
                                variant="outlined"
                                style={{
                                    margin: "5px 10px",
                                    borderRadius: 30,
                                    borderWidth: 2,
                                    borderColor: Theme.colors.lightGrey,
                                    fontFamily: Theme.fonts.type.heading,
                                    fontSize: 14,
                                    color: '#666'
                                }}
                            />
                    }
                </div>

                {
                    isCombinedMaximum ?
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <Tooltip title={ `You can spend up to $${ helpers.numberWithCommasDecimals(coverage.combinedMaximum - usage.totalUsed) } on any combination of Major Medical services.` } arrow placement="top">
                                    <InfoIcon aria-label="info" style={{ fontSize: 15, color: Theme.colors.mediumGrey, marginRight: 10 }} />
                                </Tooltip>

                                <Text small bold>
                                    { `Combined Maximum $${ helpers.numberWithCommasDecimals(coverage.combinedMaximum) }` }
                                </Text>
                            </div>

                            <Text small bold margin="0 0 10px 0" align="right">
                                { `$${ helpers.numberWithCommasDecimals(coverage.combinedMaximum - usage.totalUsed) } Remaining` }
                            </Text>
                        </div>
                        :
                        null
                }
            </div>

            {
                usage.used.map((item) => {
                    let limit = coverage[item.coverageColumn];
                    let limitText = false;
                    let extraText = '';
                    let notDollar = false;
                    let modifiedUsed = false; // for passing in a different used variable

                    if (item.coverageColumn === 'rc') {
                        limit = 50000;
                        limitText = 'Refer to your Booklet';
                    }

                    if (item.type === "Convalescent Hospital") {
                        notDollar = true;
                        limitText = `${ limit } Days`;
                    }

                    if (item.type === "Orthopedic Shoes" && coverage?.orthopedicShoesMaximumType?.toUpperCase() === 'PAIRS') {
                        if (!Number(limit)) {
                            return null;
                        }

                        notDollar = true;
                        limitText = `${ limit } Pairs`;
                    }

                    // 1 per 60 months (don't display dollar amount)
                    if (item.type === "Apnea Machine (CPAP)") {
                        limit = 1;
                        notDollar = true;
                        limitText = `1 Unit`;
                        modifiedUsed = 1; // Instead of a dollar amount, we want to show if it's been used once or not
                    }

                    // 1 per lifetime
                    if (item.type === "Artificial Eye/Limb") {
                        limit = 1;
                        notDollar = true;
                        limitText = `1 Unit`;
                        modifiedUsed = 1; // Instead of a dollar amount, we want to show if it's been used once or not
                    }

                    // When coverage is combined maximum and usage items are disabled but have usage prior to disabling 
                    if (isCombinedMaximum && Number(coverage[item.coverageColumn]) === 0 && item.used > 0) {
                        limit = modifiedUsed ? modifiedUsed : item.used;

                        if (item.type === "Convalescent Hospital") {
                            limitText = limit;
                        }
                    }

                    if (coverage[item.coverageColumn] || limitText) {
                        return (
                            <UsageBlock
                                key={ item.type }
                                usage={{
                                    type: item.type,
                                    limit: limit,
                                    used: modifiedUsed ? modifiedUsed : item.used
                                }}
                                notDollar={ notDollar }
                                extraText={ extraText }
                                fillColor={ fillColor }
                                limitText={ limitText }
                                showRemaining={ !limitText }
                            />
                        );
                    }

                    return null;
                })
            }

            {
                usage.unused.map((item) => {
                    let limit = coverage[item.coverageColumn];
                    let limitText = false;

                    let extraText = '';
                    let notDollar = false;
                    let modifiedUsed = false; // for passing in a different used variable

                    if (item.coverageColumn === 'rc') {
                        limit = 50000;
                        limitText = 'Refer to your Booklet';
                    }

                    if (item.type === "Convalescent Hospital") {
                        if (!Number(limit)) {
                            return null;
                        }

                        notDollar = true;
                        limitText = `${ limit } Days`;
                    }

                    // 1 per 60 months (don't display dollar amount)
                    if (item.type === "Apnea Machine (CPAP)") {
                        limit = 1;
                        notDollar = true;
                        limitText = `1 Unit`;
                        modifiedUsed = 0; // Instead of a dollar amount, we want to show if it's been used once or not
                    }

                    // 1 per lifetime
                    if (item.type === "Artificial Eye/Limb") {
                        limit = 1;
                        notDollar = true;
                        limitText = `1 Unit`;
                        modifiedUsed = 0; // Instead of a dollar amount, we want to show if it's been used once or not
                    }

                    // Don't show usage bar with no limit
                    if (Number(limit) === 0) {
                        return null;
                    }

                    if (coverage[item.coverageColumn] || limitText) {
                        return (
                            <UsageBlock
                                key={ item.type }
                                usage={{
                                    type: item.type,
                                    limit: limit,
                                    used: modifiedUsed ? modifiedUsed : item.used
                                }}
                                notDollar={ notDollar }
                                extraText={ extraText }
                                fillColor={ fillColor }
                                limitText={ limitText }
                            />
                        );
                    }

                    return null;
                })
            }
        </React.Fragment>
    );
};


export default HealthUsageBlock;